import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Chip, CircularProgress, FormControl, FormLabel, Input, Option, Select,
    Sheet, Stack, Textarea, Typography,
} from '@mui/joy';
import { Translate } from '@mui/icons-material';

import { PageStandard } from '../utils/PageStandard';
import { FeedbackContext } from '../utils/FeedbackContext';
import { TranslatableEntityType, TranslationEntry } from '../utils/Model';
import { getExperiences, getTranslations, saveTranslation } from '../utils/ViewModel';

const TYPE_LABELS: Record<TranslatableEntityType, string> = {
    experience_task: 'Task',
    experience_task_notification: 'Notifica',
    flow: 'Flow',
    flow_step: 'Step flow',
    experience: 'Esperienza',
    sticker: 'Sticker',
    geocache: 'Geocache',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export function Translations() {
    // Opened from within an experience (e.g. Experiences.tsx) pre-scopes the
    // list to it, same as GameFlow/ExperienceTasks — the filter can still be
    // cleared below to browse every experience (or the global catalogs).
    const { id: experienceIdParam } = useParams<{ id?: string }>();
    const { setMessage, setError } = useContext(FeedbackContext);
    const [experiences, setExperiences] = useState<{ id: string; title: string }[]>([]);
    const [entries, setEntries] = useState<TranslationEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const [experienceId, setExperienceId] = useState<string>(experienceIdParam || '');
    const [type, setType] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    useEffect(() => {
        getExperiences().then(rows => setExperiences(rows.map(r => ({ id: r.id, title: r.title }))));
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        const rows = await getTranslations({
            experience_id: experienceId || undefined,
            type: type || undefined,
            status: status || undefined,
            search: search || undefined,
        });
        setEntries(rows);
        setLoading(false);
    }, [experienceId, type, status, search]);

    useEffect(() => { refresh(); }, [refresh]);

    // Debounce the free-text search instead of refetching on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handleSave = async (entry: TranslationEntry, value: string) => {
        const ok = await saveTranslation({
            entity_type: entry.entity_type,
            entity_id: entry.entity_id,
            field_name: entry.field_name,
            value,
        });
        if (ok) {
            setMessage('Traduzione salvata');
            setEntries(prev => prev.map(e =>
                e === entry ? { ...e, translated_value: value.trim() || null } : e
            ));
        } else {
            setError('Errore nel salvataggio della traduzione');
        }
    };

    const translatedCount = entries.filter(e => e.translated_value).length;

    return (
        <PageStandard title="Traduzioni" sidebarVisible={true}>
            <Stack spacing={2.5}>
                <Typography level="body-sm" color="neutral">
                    Elenco di tutte le stringhe scritte dall'organizzatore (task, step dei flow, notifiche,
                    esperienze, sticker, geocache) che i giocatori vedono in inglese quando impostano l'app
                    in EN. L'italiano resta quello scritto normalmente — qui si aggiunge solo la traduzione
                    inglese. Se manca, i giocatori EN vedono comunque l'italiano.
                </Typography>

                {/* Filters */}
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControl sx={{ minWidth: 220 }}>
                            <FormLabel>Esperienza</FormLabel>
                            <Select
                                value={experienceId || null}
                                placeholder="Tutte le esperienze"
                                onChange={(_, v) => setExperienceId((v as string) || '')}
                            >
                                <Option value="">Tutte le esperienze</Option>
                                {experiences.map(e => (
                                    <Option key={e.id} value={e.id}>{e.title || 'Senza titolo'}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 180 }}>
                            <FormLabel>Tipo</FormLabel>
                            <Select
                                value={type || null}
                                placeholder="Tutti i tipi"
                                onChange={(_, v) => setType((v as string) || '')}
                            >
                                <Option value="">Tutti i tipi</Option>
                                {Object.entries(TYPE_LABELS).map(([k, label]) => (
                                    <Option key={k} value={k}>{label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 160 }}>
                            <FormLabel>Stato</FormLabel>
                            <Select
                                value={status || null}
                                placeholder="Tutti"
                                onChange={(_, v) => setStatus((v as string) || '')}
                            >
                                <Option value="">Tutti</Option>
                                <Option value="translated">Tradotto</Option>
                                <Option value="missing">Da tradurre</Option>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 220 }}>
                            <FormLabel>Cerca</FormLabel>
                            <Input
                                placeholder="Cerca nel testo…"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                            />
                        </FormControl>
                    </Stack>
                </Sheet>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : entries.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Translate sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
                        <Typography level="body-sm" color="neutral">
                            Nessuna stringa trovata con questi filtri.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography level="body-xs" color="neutral">
                            {translatedCount} / {entries.length} tradotte
                        </Typography>
                        <Stack spacing={1.5}>
                            {entries.map(entry => (
                                <TranslationRow
                                    key={`${entry.entity_type}:${entry.entity_id}:${entry.field_name}`}
                                    entry={entry}
                                    onSave={value => handleSave(entry, value)}
                                />
                            ))}
                        </Stack>
                    </>
                )}
            </Stack>
        </PageStandard>
    );
}

// ── TranslationRow ────────────────────────────────────────────────────────────

function TranslationRow({ entry, onSave }: { entry: TranslationEntry; onSave: (value: string) => void }) {
    const [draft, setDraft] = useState(entry.translated_value || '');

    useEffect(() => { setDraft(entry.translated_value || ''); }, [entry.translated_value]);

    const dirty = draft !== (entry.translated_value || '');
    const translated = Boolean(entry.translated_value);

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="sm" variant="soft">{TYPE_LABELS[entry.entity_type]}</Chip>
                    {entry.experience_title && (
                        <Chip size="sm" variant="outlined" color="neutral">{entry.experience_title}</Chip>
                    )}
                    <Typography level="body-sm" fontWeight="md" sx={{ flex: 1 }}>
                        {entry.context_label}
                    </Typography>
                    <Chip size="sm" color={translated ? 'success' : 'warning'} variant="soft">
                        {translated ? 'Tradotto' : 'Da tradurre'}
                    </Chip>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Italiano</FormLabel>
                        <Textarea value={entry.base_value} readOnly minRows={1} maxRows={6} color="neutral" />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>English</FormLabel>
                        <Textarea
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            onBlur={() => { if (dirty) onSave(draft); }}
                            placeholder="Traduzione inglese…"
                            minRows={1}
                            maxRows={6}
                            color={dirty ? 'primary' : 'neutral'}
                        />
                    </FormControl>
                </Stack>
            </Stack>
        </Sheet>
    );
}
